import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import ValetApi from "../api/Api";
import { toast } from "react-toastify";
import { ensurePermission, isSupported as isPushSupported, subscribe as subscribeToPush, unsubscribeCurrent } from "../utility/pushNotifications";
import { clearGarageSnapshots } from "../utility/garageOfflineCache";
const UserContext = createContext();

const SESSION_LAST_ACTIVITY_KEY = "session.lastActivityAt";
const INACTIVITY_AUTO_LOGOUT_MS = 3 * 60 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000;

function getNotificationPermissionSnapshot() {
    if (typeof Notification === "undefined") return "default";
    return Notification.permission;
}

function getUserScopedStorageKey(username, baseKey) {
    const normalizedUsername = String(username || "").trim().toLowerCase();
    return normalizedUsername ? `${baseKey}:${normalizedUsername}` : baseKey;
}

function getUserScopedStorageKeyById(userId, baseKey) {
    const normalizedUserId = Number(userId);
    return Number.isFinite(normalizedUserId) && normalizedUserId > 0 ? `${baseKey}:userId:${normalizedUserId}` : baseKey;
}

function readStoredActiveSelection(username, userId = null) {
    const normalizedUsername = String(username || "").trim().toLowerCase();
    const normalizedUserId = Number(userId);
    const hasUserIdentity = Boolean(normalizedUsername) || (Number.isFinite(normalizedUserId) && normalizedUserId > 0);

    const scopedPodiumKey = getUserScopedStorageKey(username, "activePodiumId");
    const scopedLocationKey = getUserScopedStorageKey(username, "activeLocationId");
    const scopedPodiumIdKeyByUserId = getUserScopedStorageKeyById(userId, "activePodiumId");
    const scopedLocationIdKeyByUserId = getUserScopedStorageKeyById(userId, "activeLocationId");

    const candidatePodiumKeys = hasUserIdentity ? [scopedPodiumKey, scopedPodiumIdKeyByUserId] : ["activePodiumId"];
    const candidateLocationKeys = hasUserIdentity ? [scopedLocationKey, scopedLocationIdKeyByUserId] : ["activeLocationId"];

    const storedPodiumId = Number(candidatePodiumKeys.map((key) => localStorage.getItem(key)).find((value) => value !== null && value !== "") || 0) || null;
    const storedLocationId = Number(candidateLocationKeys.map((key) => localStorage.getItem(key)).find((value) => value !== null && value !== "") || 0) || null;

    return {
        podiumId: storedPodiumId,
        locationId: storedLocationId,
        scopedPodiumKey,
        scopedLocationKey,
        scopedPodiumIdKeyByUserId,
        scopedLocationIdKeyByUserId,
    };
}

function writeStoredActiveSelection(username, userId, podiumId, locationId) {
    let normalizedUserId = userId;
    let normalizedPodiumId = podiumId;
    let normalizedLocationId = locationId;

    if (arguments.length === 3) {
        normalizedUserId = null;
        normalizedPodiumId = userId;
        normalizedLocationId = podiumId;
    }

    const scopedPodiumKey = getUserScopedStorageKey(username, "activePodiumId");
    const scopedLocationKey = getUserScopedStorageKey(username, "activeLocationId");
    const scopedPodiumIdKeyByUserId = getUserScopedStorageKeyById(normalizedUserId, "activePodiumId");
    const scopedLocationIdKeyByUserId = getUserScopedStorageKeyById(normalizedUserId, "activeLocationId");
    const hasUserIdentity = Boolean(String(username || "").trim()) || (Number.isFinite(normalizedUserId) && normalizedUserId > 0);

    if (normalizedPodiumId && normalizedLocationId) {
        localStorage.setItem(scopedPodiumKey, String(normalizedPodiumId));
        localStorage.setItem(scopedLocationKey, String(normalizedLocationId));
        localStorage.setItem(scopedPodiumIdKeyByUserId, String(normalizedPodiumId));
        localStorage.setItem(scopedLocationIdKeyByUserId, String(normalizedLocationId));
        if (!hasUserIdentity) {
            localStorage.setItem("activePodiumId", String(normalizedPodiumId));
            localStorage.setItem("activeLocationId", String(normalizedLocationId));
        }
        return;
    }

    localStorage.removeItem(scopedPodiumKey);
    localStorage.removeItem(scopedLocationKey);
    localStorage.removeItem(scopedPodiumIdKeyByUserId);
    localStorage.removeItem(scopedLocationIdKeyByUserId);
    localStorage.removeItem("activePodiumId");
    localStorage.removeItem("activeLocationId");
}

function hasPushCapableRole(userDetail) {
    const roleId = Number(userDetail?.roleId || 0);
    return roleId >= 1 && roleId <= 5;
}

export const UserProvider = ({ children }) => {
    const history = useHistory();
    const inactivityIntervalRef = useRef(null);
    const autoLogoutTriggeredRef = useRef(false);
    const lastActivityWriteRef = useRef(0);
    const pushSyncInFlightRef = useRef(false);
    const lastPushSyncSignatureRef = useRef("");

    const [token, setToken] = useState(() => localStorage.getItem("token") || null);

    const [userDetail, setUserDetail] = useState(() => {
        try {
            const item = localStorage.getItem("userDetail");
            return item ? JSON.parse(item) : null;
        } catch (error) {
            return null;
        }
    });

    const [error, setError] = useState(null);
    const [refreshRequestedQueue, setRefreshRequestedQueue] = useState(false);
    const [garageData, setGarageData] = useState([]);

    const [activePodiumId, setActivePodiumId] = useState(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem("userDetail") || "null");
            const username = String(storedUser?.username || "").trim().toLowerCase();
            const userId = Number(storedUser?.id || 0);
            const { podiumId } = readStoredActiveSelection(username, userId);
            return Number.isFinite(Number(podiumId)) && Number(podiumId) > 0 ? Number(podiumId) : null;
        } catch {
            const stored = localStorage.getItem("activePodiumId");
            return stored ? Number(stored) : null;
        }
    });
    const [activeLocationId, setActiveLocationId] = useState(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem("userDetail") || "null");
            const username = String(storedUser?.username || "").trim().toLowerCase();
            const userId = Number(storedUser?.id || 0);
            const { locationId } = readStoredActiveSelection(username, userId);
            return Number.isFinite(Number(locationId)) && Number(locationId) > 0 ? Number(locationId) : null;
        } catch {
            const stored = localStorage.getItem("activeLocationId");
            return stored ? Number(stored) : null;
        }
    });
    const [pushNotificationStatus, setPushNotificationStatus] = useState(() => ({
        supported: isPushSupported(),
        enabled: false,
        subscribed: false,
        permission: getNotificationPermissionSnapshot(),
    }));

    const clearInactivityInterval = useCallback(() => {
        if (inactivityIntervalRef.current) {
            window.clearInterval(inactivityIntervalRef.current);
            inactivityIntervalRef.current = null;
        }
    }, []);

    const clearClientSession = useCallback(() => {
        setToken(null);
        setUserDetail(null);
        setActivePodiumId(null);
        setActiveLocationId(null);
        localStorage.removeItem("token");
        void clearGarageSnapshots();
        setGarageData([]);
        localStorage.removeItem("userDetail");
        localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
        ValetApi.token = null;
        autoLogoutTriggeredRef.current = false;
        pushSyncInFlightRef.current = false;
        lastPushSyncSignatureRef.current = "";
        setPushNotificationStatus({
            supported: isPushSupported(),
            enabled: false,
            subscribed: false,
            permission: getNotificationPermissionSnapshot(),
        });
    }, []);

    const buildCombinedUserDetail = (baseUser, podiumRows, locationRows, userTimeZone) => ({
        ...baseUser,
        accessiblePodiums: podiumRows.map((podium) => ({
            id: podium.podiumId,
            name: podium.podiumName,
            locationId: podium.locationId,
        })),
        accessibleLocations: locationRows.map((location) => ({
            id: location.locationId,
            name: location.locationName,
            address: location.address,
            city: location.city,
            state: location.state,
            zipCode: location.zipCode,
            phone: location.phone,
            regionId: location.regionId,
        })),
        userTimeZone,
    });

    const applyActiveSelectionFromUserDetail = useCallback((combinedUserDetail) => {
        const username = String(combinedUserDetail?.username || userDetail?.username || "").trim().toLowerCase();
        const userId = Number(combinedUserDetail?.id || userDetail?.id || 0);
        const { podiumId: storedPodiumId, locationId: storedLocationId, scopedPodiumKey, scopedLocationKey, scopedPodiumIdKeyByUserId, scopedLocationIdKeyByUserId } = readStoredActiveSelection(username, userId);
        const accessiblePodiums = combinedUserDetail.accessiblePodiums || [];
        const hasStoredPodiumAccess = Boolean(storedPodiumId) && accessiblePodiums.some((podium) => Number(podium.id) === Number(storedPodiumId));

        const homePodiumId = Number(combinedUserDetail.podiumId) || null;
        const homePodium = accessiblePodiums.find((podium) => Number(podium.id) === homePodiumId);
        const firstAccessiblePodium = accessiblePodiums[0] || null;

        const fallbackPodiumId = homePodiumId || Number(firstAccessiblePodium?.id) || null;
        const fallbackLocationId = Number(homePodium?.locationId) || Number(combinedUserDetail.locationId) || Number(firstAccessiblePodium?.locationId) || null;

        const nextActivePodiumId = hasStoredPodiumAccess ? storedPodiumId : fallbackPodiumId;
        const nextActiveLocationId = hasStoredPodiumAccess ? Number(storedLocationId || fallbackLocationId) : Number(fallbackLocationId);

        setActivePodiumId(nextActivePodiumId);
        setActiveLocationId(nextActiveLocationId);

        if (nextActivePodiumId && nextActiveLocationId) {
            localStorage.setItem(scopedPodiumKey, String(nextActivePodiumId));
            localStorage.setItem(scopedLocationKey, String(nextActiveLocationId));
            localStorage.setItem(scopedPodiumIdKeyByUserId, String(nextActivePodiumId));
            localStorage.setItem(scopedLocationIdKeyByUserId, String(nextActiveLocationId));
            localStorage.setItem("activePodiumId", String(nextActivePodiumId));
            localStorage.setItem("activeLocationId", String(nextActiveLocationId));
        } else {
            localStorage.removeItem(scopedPodiumKey);
            localStorage.removeItem(scopedLocationKey);
            localStorage.removeItem(scopedPodiumIdKeyByUserId);
            localStorage.removeItem(scopedLocationIdKeyByUserId);
            localStorage.removeItem("activePodiumId");
            localStorage.removeItem("activeLocationId");
        }
    }, [userDetail?.username, userDetail?.id]);

    const refreshCurrentSession = useCallback(
        async ({ nextToken = null, silent = true } = {}) => {
            const currentUsername = String(userDetail?.username || "")
                .trim()
                .toLowerCase();

            if (!currentUsername) {
                return { success: false };
            }

            try {
                if (nextToken) {
                    ValetApi.token = nextToken;
                    setToken(nextToken);
                    localStorage.setItem("token", nextToken);
                }

                const userRes = await ValetApi.getUser(currentUsername);
                const [podiumsRes, locationsRes] = await Promise.all([ValetApi.getUserPodiums(userRes.data.user.id), ValetApi.getUserLocations(userRes.data.user.id)]);

                const nextUserTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                const combinedUserDetail = buildCombinedUserDetail(userRes.data.user, Array.isArray(podiumsRes?.data) ? podiumsRes.data : [], Array.isArray(locationsRes?.data) ? locationsRes.data : [], nextUserTimeZone);

                setUserDetail(combinedUserDetail);
                localStorage.setItem("userDetail", JSON.stringify(combinedUserDetail));
                applyActiveSelectionFromUserDetail(combinedUserDetail);
                localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));

                return { success: true, userDetail: combinedUserDetail };
            } catch (err) {
                if (!silent) {
                    toast.error(err[0] || err.response?.data?.error?.message || "Unable to refresh session.", { autoClose: 1200 });
                }
                return { success: false };
            }
        },
        [applyActiveSelectionFromUserDetail, userDetail?.username, userDetail?.userTimeZone],
    );

    const markSessionActivity = useCallback(() => {
        if (!token) return;

        const now = Date.now();
        if (now - lastActivityWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
        lastActivityWriteRef.current = now;
        localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
    }, [token]);

    // ---- USER  ----
    const login = async (username, password) => {
        try {
            const userNameLower = username.trim().toLowerCase();
            const res = await ValetApi.validateUser(userNameLower, password);

            ValetApi.token = res.data.token;
            setToken(res.data.token);
            localStorage.setItem("token", res.data.token);

            const userRes = await ValetApi.getUser(userNameLower);

            if (userRes.data.user.status !== "active") {
                toast.error("Your account is not yet active. Please wait for admin approval or check with your administrator", { autoClose: 3000 });
                return { success: false };
            }

            const [podiumsRes, locationsRes] = await Promise.all([ValetApi.getUserPodiums(userRes.data.user.id), ValetApi.getUserLocations(userRes.data.user.id)]);

            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const combinedUserDetail = buildCombinedUserDetail(userRes.data.user, Array.isArray(podiumsRes?.data) ? podiumsRes.data : [], Array.isArray(locationsRes?.data) ? locationsRes.data : [], userTimeZone);

            applyActiveSelectionFromUserDetail(combinedUserDetail);

            setUserDetail(combinedUserDetail);

            localStorage.setItem("userDetail", JSON.stringify(combinedUserDetail));
            localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));

            // Navigate to what page based on roleId
            const roleId = combinedUserDetail.roleId;
            if (roleId === 1) {
                history.push("/valetDashBoard");
            } else if (roleId === 2) {
                history.push("/garage");
            } else if (roleId >= 3) {
                history.push("/dashboard");
            } else {
                history.push("/"); // fallback
            }
            toast.success(`Welcome back, ${userRes.data.user.firstName}!`, { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Login Error: Invalid Username / Password", { autoClose: 1000 });
            return { success: false };
        }
    };

    const loginTenantPortal = async (username, password) => {
        try {
            const normalizedUsername = String(username || "")
                .trim()
                .toLowerCase();
            const res = await ValetApi.validateTenantPortalUser(normalizedUsername, password);

            ValetApi.token = res.data.token;
            setToken(res.data.token);
            localStorage.setItem("token", res.data.token);

            const profile = res?.data?.profile || {};
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const tenantUserDetail = {
                username: profile.portalUsername || normalizedUsername,
                firstName: profile.contactName || profile.name || "Tenant",
                roleId: 0,
                isTenantPortal: true,
                tenantId: Number(profile.id),
                tenantName: profile.name,
                locationId: Number(profile.locationId),
                accessiblePodiums: [],
                accessibleLocations: [
                    {
                        id: Number(profile.locationId),
                        name: profile.locationName || `Location ${profile.locationId}`,
                    },
                ],
                userTimeZone,
            };

            setUserDetail(tenantUserDetail);
            localStorage.setItem("userDetail", JSON.stringify(tenantUserDetail));

            setActivePodiumId(null);
            setActiveLocationId(Number(profile.locationId) || null);
            localStorage.removeItem("activePodiumId");
            if (profile.locationId) {
                localStorage.setItem("activeLocationId", String(profile.locationId));
            } else {
                localStorage.removeItem("activeLocationId");
            }

            localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));

            history.push("/tenant/portal");
            toast.success(`Welcome, ${tenantUserDetail.firstName}`, { autoClose: 1200 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Tenant login failed", { autoClose: 1200 });
            return { success: false };
        }
    };

    const register = async (username, password, firstName, lastName, email, phone, roleId, podiumId) => {
        try {
            const usernameLower = username.trim().toLowerCase();
            const emailLower = email.trim().toLowerCase();
            await ValetApi.registerUser(usernameLower, password, firstName, lastName, emailLower, phone, roleId, podiumId);

            history.push("/login");
            toast.success(`Registration successful!, Account Pending Approval`, { autoClose: 5000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Register Error: Invalid username / password provided Code: Default", { autoClose: 1000 });
            return { success: false };
        }
    };

    const updateUser = async (username, data) => {
        try {
            const res = await ValetApi.updateUser(username, { ...data });
            setUserDetail(res.data.user);
            localStorage.setItem("userDetail", JSON.stringify(res.data.user));
            toast.success("User updated successfully", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid username / password provided Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };

    const logout = async () => {
        try {
            if (isPushSupported()) {
                // Never block logout on service worker/push cleanup in standalone mobile contexts.
                void unsubscribeCurrent({ timeoutMs: 1200 }).catch(() => false);
            }

            clearInactivityInterval();
            clearClientSession();
            history.push("/");

            toast.success("Logged out successfully", { autoClose: 1000 });
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Error logging out", { autoClose: 1000 });
        }
    };

    const enablePushNotifications = async () => {
        try {
            if (!token || !userDetail || Boolean(userDetail.isTenantPortal) || !hasPushCapableRole(userDetail) || !isPushSupported()) {
                setPushNotificationStatus((prev) => ({
                    ...prev,
                    supported: isPushSupported(),
                    enabled: false,
                    subscribed: false,
                    permission: getNotificationPermissionSnapshot(),
                }));
                return { success: false };
            }

            const scopedPodiumId = Number(activePodiumId || userDetail.podiumId || 0) || null;
            const scopedLocationId = Number(activeLocationId || userDetail.locationId || 0) || null;

            if (!scopedLocationId) {
                toast.error("Please select an active location first.", { autoClose: 1200 });
                return { success: false };
            }

            pushSyncInFlightRef.current = true;

            const publicKeyRes = await ValetApi.getPushPublicKey();
            const enabled = Boolean(publicKeyRes?.data?.enabled);
            const vapidPublicKey = String(publicKeyRes?.data?.publicKey || "").trim();
            if (!enabled || !vapidPublicKey) {
                setPushNotificationStatus((prev) => ({
                    ...prev,
                    enabled: false,
                    subscribed: false,
                    permission: getNotificationPermissionSnapshot(),
                }));
                toast.error("Push notifications are not enabled on the server.", { autoClose: 1500 });
                return { success: false };
            }

            setPushNotificationStatus((prev) => ({
                ...prev,
                supported: true,
                enabled: true,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            }));

            const permission = await ensurePermission();
            if (permission !== "granted") {
                setPushNotificationStatus((prev) => ({
                    ...prev,
                    enabled: true,
                    subscribed: false,
                    permission,
                }));
                return { success: false };
            }

            const subscription = await subscribeToPush({ vapidPublicKey });
            if (!subscription) {
                setPushNotificationStatus((prev) => ({
                    ...prev,
                    enabled: true,
                    subscribed: false,
                    permission: "granted",
                }));
                return { success: false };
            }

            await ValetApi.subscribePushNotifications({
                subscription: subscription.toJSON(),
                podiumId: scopedPodiumId,
                locationId: scopedLocationId,
                deviceLabel: typeof navigator !== "undefined" ? navigator.userAgent : "",
            });

            setPushNotificationStatus((prev) => ({
                ...prev,
                supported: true,
                enabled: true,
                subscribed: true,
                permission: "granted",
            }));

            const syncSignature = `${Number(userDetail.id || 0)}:${Number(scopedPodiumId || 0)}:${Number(scopedLocationId || 0)}:granted`;
            lastPushSyncSignatureRef.current = syncSignature;

            toast.success("Push alerts enabled on this device.", { autoClose: 1200 });
            return { success: true };
        } catch (_err) {
            setPushNotificationStatus((prev) => ({
                ...prev,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            }));
            toast.error("Unable to enable push alerts on this device.", { autoClose: 1400 });
            return { success: false };
        } finally {
            pushSyncInFlightRef.current = false;
        }
    };

    // ---- VEHICLE ----
    const addVehicle = async (ticketNum, nextStatusId, mobile, color, make, damages, notes) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            const normalizedTicketNum = Number(ticketNum);
            if (!Number.isInteger(normalizedTicketNum) || normalizedTicketNum <= 0) {
                toast.error("Ticket number must be a non-zero number.", { autoClose: 1000 });
                return { success: false };
            }

            // 1. Create the vehicle
            const addVehicleRes = await ValetApi.createVehicle(Number(normalizedTicketNum), 1, mobile, color, make, damages, notes);
            const vehicleId = addVehicleRes.data.vehicle.id;

            // 2. Create checked_in transaction
            await ValetApi.createTransaction(Number(userDetail.id), Number(vehicleId), Number(activePodiumId), Number(activeLocationId), 1);

            // 3. If nextStatusId is 2 or 3, create a second transaction
            if (Number(nextStatusId) === 2 || Number(nextStatusId) === 3) {
                await ValetApi.updateVehicle(vehicleId, { statusId: Number(nextStatusId) });
                await ValetApi.createTransaction(Number(userDetail.id), Number(vehicleId), Number(activePodiumId), Number(activeLocationId), Number(nextStatusId));
            }

            // 4. increment parked
            if (Number(nextStatusId) === 3) {
                setUserDetail({ ...userDetail, totalParked: userDetail.totalParked + 1 });
            }
            // 5. Display success message
            toast.success("Vehicle added successfully", { autoClose: 1000 });
            setError(null);

            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "AddVehicle Error: Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };

    const startVehicleCheckIn = async (ticketNum, mobile) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            const normalizedTicketNum = Number(ticketNum);
            if (!Number.isInteger(normalizedTicketNum) || normalizedTicketNum <= 0) {
                toast.error("Ticket number must be a non-zero number.", { autoClose: 1000 });
                return { success: false };
            }

            const normalizedMobile = String(mobile || "")
                .replace(/\D/g, "")
                .slice(0, 10);

            if (normalizedMobile.length < 10) {
                toast.error("Customer phone number is required.", { autoClose: 1000 });
                return { success: false };
            }

            const createRes = await ValetApi.createVehicle(Number(normalizedTicketNum), 1, normalizedMobile, undefined, undefined, "[]", "");
            const vehicle = createRes?.data?.vehicle || null;
            const vehicleId = Number(vehicle?.id || 0);

            if (!vehicleId) {
                toast.error("Unable to start check-in. Please try again.", { autoClose: 1000 });
                return { success: false };
            }

            const checkInTransactionRes = await ValetApi.createTransaction(Number(userDetail.id), vehicleId, Number(activePodiumId), Number(activeLocationId), 1);
            const checkInTransactionId = Number(checkInTransactionRes?.data?.transaction?.id || 0);

            toast.success("Step 1 complete. Customer text has been triggered.", { autoClose: 1000 });
            setError(null);

            return {
                success: true,
                vehicleId,
                vehicle,
                checkInTransactionId: Number.isInteger(checkInTransactionId) && checkInTransactionId > 0 ? checkInTransactionId : null,
            };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Check-in start failed", { autoClose: 1000 });
            return { success: false };
        }
    };

    const completeVehicleCheckIn = async (vehicleId, nextStatusId, color, make, damages, notes) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            const normalizedVehicleId = Number(vehicleId);
            if (!Number.isInteger(normalizedVehicleId) || normalizedVehicleId <= 0) {
                toast.error("Invalid check-in session. Please restart check-in.", { autoClose: 1000 });
                return { success: false };
            }

            const normalizedNextStatusId = Number(nextStatusId);
            const normalizedColor = String(color || "")
                .trim()
                .toLowerCase();
            if (!normalizedColor) {
                toast.error("Vehicle color is required.", { autoClose: 1000 });
                return { success: false };
            }

            const normalizedMake = String(make || "").trim();
            if (!normalizedMake) {
                toast.error("Vehicle make is required.", { autoClose: 1000 });
                return { success: false };
            }

            const updatePayload = {
                color: normalizedColor,
                make: normalizedMake,
                damages: typeof damages === "string" ? damages : JSON.stringify(damages || []),
                notes: String(notes || "").trim(),
            };

            if (normalizedNextStatusId === 2 || normalizedNextStatusId === 3) {
                updatePayload.statusId = normalizedNextStatusId;
            }

            await ValetApi.updateVehicle(normalizedVehicleId, updatePayload);

            if (normalizedNextStatusId === 2 || normalizedNextStatusId === 3) {
                await ValetApi.createTransaction(Number(userDetail.id), normalizedVehicleId, Number(activePodiumId), Number(activeLocationId), normalizedNextStatusId);
            }

            if (normalizedNextStatusId === 3) {
                setUserDetail((prev) => ({
                    ...prev,
                    totalParked: Number(prev?.totalParked || 0) + 1,
                }));
            }

            toast.success("Vehicle check-in complete", { autoClose: 1000 });
            setError(null);
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Check-in completion failed", { autoClose: 1000 });
            return { success: false };
        }
    };

    // check arguments, podiumId and locationId passed in, will use activePodiumId and activeLocationId
    const stageVehicle = async (userId, vehicleId, podiumId, locationId) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            await ValetApi.updateVehicle(vehicleId, { statusId: 2 });
            await ValetApi.createTransaction(userId, vehicleId, activePodiumId, activeLocationId, 2);
            setRefreshRequestedQueue((q) => !q);
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Stage Vehicle Error: Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };
    // check arguments, podiumId and locationId passed in, will use activePodiumId and activeLocationId
    const parkVehicle = async (userId, vehicleId, podiumId, locationId, parkingSpot = null) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            await ValetApi.updateVehicle(vehicleId, { statusId: 3 });
            await ValetApi.createTransaction(userId, vehicleId, activePodiumId, activeLocationId, 3, { parkingSpot });
            setUserDetail({ ...userDetail, totalParked: userDetail.totalParked + 1 });
            setRefreshRequestedQueue((q) => !q);
            // toast.success("Vehicle parked successfully", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };
    // check arguments, podiumId and locationId passed in, will use activePodiumId and activeLocationId
    const requestVehicle = async (userId, vehicleId, podiumId, locationId) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            await ValetApi.createTransaction(userId, vehicleId, activePodiumId, activeLocationId, 4);
            await ValetApi.updateVehicle(vehicleId, { statusId: 4 });
            setRefreshRequestedQueue((q) => !q);
            history.push("/valetDashBoard");
            toast.success("Vehicle requested", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };
    // check arguments, podiumId and locationId passed in, will use activePodiumId and activeLocationId
    const retrieveVehicle = async (userId, vehicleId, podiumId, locationId) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            await ValetApi.updateVehicle(vehicleId, { statusId: 5 });
            await ValetApi.createTransaction(userId, vehicleId, activePodiumId, activeLocationId, 5);
            setRefreshRequestedQueue((q) => !q);
            history.push("/valetDashBoard");
            toast.success("Vehicle retrieved", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };

    // check arguments, podiumId and locationId passed in, will use activePodiumId and activeLocationId
    const checkoutVehicle = async (userId, vehicleId, podiumId, locationId) => {
        try {
            if (!requireActiveSelection()) return { success: false };

            await ValetApi.updateVehicle(vehicleId, { statusId: 6 });
            await ValetApi.createTransaction(userId, vehicleId, activePodiumId, activeLocationId, 6);
            setRefreshRequestedQueue((q) => !q);
            history.push("/valetDashBoard");
            toast.success("Vehicle checked out", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };

    // ---- LOCATION METHODS ----
    const addRegion = async (name) => {
        try {
            const res = await ValetApi.createRegion(name);
            await refreshCurrentSession({
                nextToken: res?.data?.token || null,
                silent: true,
            });

            toast.success("Region added successfully!", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Error adding region.", { autoClose: 1000 });
            return { success: false };
        }
    };

    const addLocation = async ({ name, address, city, state, zipCode, phone, regionId }) => {
        try {
            const res = await ValetApi.createLocation({ name, address, city, state, zipCode, phone, regionId: Number(regionId) });
            await refreshCurrentSession({
                nextToken: res?.data?.token || null,
                silent: true,
            });
            toast.success(`Location ${name} added successfully`, { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Invalid username / password provided Code: default", { autoClose: 1000 });
            return { success: false };
        }
    };

    const addPodium = async ({ name, locationId, isDeleted = false, parkingCapacity = null }) => {
        try {
            const res = await ValetApi.createPodium(name, Number(locationId), isDeleted, parkingCapacity);
            await refreshCurrentSession({
                nextToken: res?.data?.token || null,
                silent: true,
            });

            toast.success("Podium added successfully!", { autoClose: 1000 });
            return { success: true };
        } catch (err) {
            toast.error(err[0] || err.response?.data?.error?.message || "Error adding podium.", { autoClose: 1000 });
            return { success: false };
        }
    };

    // ---- PODIUM / LOCATION SELECTION ----
    const selectPodium = (podiumId, locationId) => {
        const nextPodiumId = Number(podiumId) || null;

        let nextLocationId = Number(locationId) || null;
        if (!nextLocationId && nextPodiumId) {
            const selected = (userDetail?.accessiblePodiums || []).find((p) => Number(p.id) === nextPodiumId);
            nextLocationId = Number(selected?.locationId) || Number(userDetail?.locationId) || null;
        }

        if (!nextPodiumId || !nextLocationId) {
            toast.error("Please select an active location and podium first.", { autoClose: 1000 });
            return false;
        }

        const username = String(userDetail?.username || "").trim().toLowerCase();
        const userId = Number(userDetail?.id || 0);
        setActivePodiumId(nextPodiumId);
        setActiveLocationId(nextLocationId);

        writeStoredActiveSelection(username, userId, nextPodiumId, nextLocationId);
        return true;
    };

    const requireActiveSelection = () => {
        if (!activePodiumId || !activeLocationId) {
            toast.error("Please select an active location and podium first.", { autoClose: 1000 });
            return false;
        }
        return true;
    };
    // Self heal: always restore the current user's persisted podium when it is valid, and only fall back to the default podium when no persisted value exists.
    useEffect(() => {
        if (!userDetail) return;

        const username = String(userDetail?.username || "").trim().toLowerCase();
        const userId = Number(userDetail?.id || 0);
        const storedSelection = readStoredActiveSelection(username, userId);
        const persistedPodiumId = Number(storedSelection?.podiumId || 0);
        const persistedLocationId = Number(storedSelection?.locationId || 0);
        const accessiblePodiums = userDetail?.accessiblePodiums || [];
        const hasPersistedSelection = Boolean(persistedPodiumId) && accessiblePodiums.some((podium) => Number(podium.id) === persistedPodiumId);

        if (hasPersistedSelection) {
            const nextPodiumId = Number(persistedPodiumId);
            const nextLocationId = Number(persistedLocationId || userDetail.locationId || 0) || null;

            if (Number(activePodiumId) !== nextPodiumId || Number(activeLocationId) !== nextLocationId) {
                setActivePodiumId(nextPodiumId);
                setActiveLocationId(nextLocationId);
            }
            return;
        }

        if (!activePodiumId && userDetail.podiumId) {
            setActivePodiumId(Number(userDetail.podiumId));
        }

        if (!activeLocationId && userDetail.locationId) {
            setActiveLocationId(Number(userDetail.locationId));
        }
    }, [userDetail, activePodiumId, activeLocationId]);

    useEffect(() => {
        if (!token) {
            clearInactivityInterval();
            return;
        }

        autoLogoutTriggeredRef.current = false;

        const now = Date.now();
        const storedLastActivity = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY) || 0);
        const initialLastActivity = Number.isInteger(storedLastActivity) && storedLastActivity > 0 ? storedLastActivity : now;

        lastActivityWriteRef.current = initialLastActivity;
        localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(initialLastActivity));

        const logoutForInactivity = () => {
            if (autoLogoutTriggeredRef.current) return;
            autoLogoutTriggeredRef.current = true;

            clearInactivityInterval();
            clearClientSession();
            history.push("/");
            toast.info("Logged out after 3 hours of inactivity. Please sign in again.", { autoClose: 2000 });
        };

        const checkInactivity = () => {
            const lastActivityAt = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY) || 0);
            const lastSeenAt = Number.isInteger(lastActivityAt) && lastActivityAt > 0 ? lastActivityAt : Date.now();

            if (Date.now() - lastSeenAt >= INACTIVITY_AUTO_LOGOUT_MS) {
                logoutForInactivity();
            }
        };

        const handleActivity = () => {
            markSessionActivity();
        };

        const handleStorageSync = (event) => {
            if (event.key !== SESSION_LAST_ACTIVITY_KEY) return;

            const nextValue = Number(event.newValue || 0);
            if (Number.isInteger(nextValue) && nextValue > lastActivityWriteRef.current) {
                lastActivityWriteRef.current = nextValue;
            }
        };

        const activityEvents = ["mousedown", "keydown", "touchstart", "wheel", "scroll"];
        activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
        window.addEventListener("storage", handleStorageSync);

        checkInactivity();
        inactivityIntervalRef.current = window.setInterval(checkInactivity, 60 * 1000);

        return () => {
            activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
            window.removeEventListener("storage", handleStorageSync);
            clearInactivityInterval();
        };
    }, [token, clearClientSession, clearInactivityInterval, history, markSessionActivity]);

    useEffect(() => {
        ValetApi.token = token || null;
    }, [token]);

    useEffect(() => {
        if (!token) {
            lastPushSyncSignatureRef.current = "";
            setPushNotificationStatus({
                supported: isPushSupported(),
                enabled: false,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            });
            return;
        }

        if (!userDetail || Boolean(userDetail.isTenantPortal)) {
            setPushNotificationStatus((prev) => ({
                ...prev,
                supported: isPushSupported(),
                enabled: false,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            }));
            return;
        }

        const roleId = Number(userDetail.roleId || 0);
        if (roleId < 1 || roleId > 5) {
            setPushNotificationStatus((prev) => ({
                ...prev,
                supported: isPushSupported(),
                enabled: false,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            }));
            return;
        }

        const pushSupported = isPushSupported();
        if (!pushSupported) {
            setPushNotificationStatus((prev) => ({
                ...prev,
                supported: false,
                enabled: false,
                subscribed: false,
                permission: getNotificationPermissionSnapshot(),
            }));
            return;
        }

        const permissionSnapshot = getNotificationPermissionSnapshot();
        setPushNotificationStatus((prev) => ({
            ...prev,
            supported: true,
            permission: permissionSnapshot,
        }));

        if (permissionSnapshot === "denied") {
            setPushNotificationStatus((prev) => ({
                ...prev,
                enabled: true,
                subscribed: false,
                permission: permissionSnapshot,
            }));
            return;
        }

        const scopedPodiumId = Number(activePodiumId || userDetail.podiumId || 0) || null;
        const scopedLocationId = Number(activeLocationId || userDetail.locationId || 0) || null;

        if (!scopedLocationId) {
            return;
        }

        const syncSignature = `${Number(userDetail.id || 0)}:${Number(scopedPodiumId || 0)}:${Number(scopedLocationId || 0)}:${permissionSnapshot}`;
        if (lastPushSyncSignatureRef.current === syncSignature || pushSyncInFlightRef.current) {
            return;
        }

        let cancelled = false;

        const syncPushSubscription = async () => {
            pushSyncInFlightRef.current = true;

            try {
                const publicKeyRes = await ValetApi.getPushPublicKey();
                const enabled = Boolean(publicKeyRes?.data?.enabled);
                const vapidPublicKey = String(publicKeyRes?.data?.publicKey || "").trim();
                if (!enabled || !vapidPublicKey) {
                    setPushNotificationStatus((prev) => ({
                        ...prev,
                        enabled: false,
                        subscribed: false,
                        permission: getNotificationPermissionSnapshot(),
                    }));
                    return;
                }

                setPushNotificationStatus((prev) => ({
                    ...prev,
                    enabled: true,
                    permission: getNotificationPermissionSnapshot(),
                }));

                if (permissionSnapshot !== "granted") {
                    setPushNotificationStatus((prev) => ({
                        ...prev,
                        enabled: true,
                        subscribed: false,
                        permission: permissionSnapshot,
                    }));
                    return;
                }

                const subscription = await subscribeToPush({ vapidPublicKey });
                if (!subscription) {
                    return;
                }

                await ValetApi.subscribePushNotifications({
                    subscription: subscription.toJSON(),
                    podiumId: scopedPodiumId,
                    locationId: scopedLocationId,
                    deviceLabel: typeof navigator !== "undefined" ? navigator.userAgent : "",
                });

                setPushNotificationStatus((prev) => ({
                    ...prev,
                    supported: true,
                    enabled: true,
                    subscribed: true,
                    permission: "granted",
                }));

                if (!cancelled) {
                    lastPushSyncSignatureRef.current = syncSignature;
                }
            } catch (_err) {
                // Suppress push sync errors so they never interrupt active valet workflows.
                setPushNotificationStatus((prev) => ({
                    ...prev,
                    subscribed: false,
                    permission: getNotificationPermissionSnapshot(),
                }));
            } finally {
                pushSyncInFlightRef.current = false;
            }
        };

        syncPushSubscription();

        return () => {
            cancelled = true;
        };
    }, [token, userDetail, activePodiumId, activeLocationId]);

    return (
        <UserContext.Provider
            value={{
                token,
                userDetail,
                error,
                login,
                loginTenantPortal,
                register,
                updateUser,
                logout,
                addVehicle,
                startVehicleCheckIn,
                completeVehicleCheckIn,
                stageVehicle,
                parkVehicle,
                retrieveVehicle,
                requestVehicle,
                checkoutVehicle,
                addRegion,
                addLocation,
                addPodium,
                setError,
                setUserDetail,
                setToken,
                refreshRequestedQueue,
                setRefreshRequestedQueue,
                garageData,
                setGarageData,
                activePodiumId,
                activeLocationId,
                setActivePodiumId,
                setActiveLocationId,
                selectPodium,
                accessiblePodiums: userDetail?.accessiblePodiums || [],
                accessibleLocations: userDetail?.accessibleLocations || [],
                isTenantPortal: Boolean(userDetail?.isTenantPortal),
                requireActiveSelection,
                pushNotificationStatus,
                enablePushNotifications,
            }}>
            {children}
        </UserContext.Provider>
    );
};

export { getUserScopedStorageKey, readStoredActiveSelection, writeStoredActiveSelection };
export default UserContext;
